import { KeyboardTrigger } from '../Layout';
import Button from '../PendantButton';
import { ButtonProps, ConfirmAction, ContentFooter } from './ContentItem';
import { COLOR, getChangeWrapperString } from '../shared/shared';
import {
	ReactElement,
	useRef,
	useState,
	useMemo,
} from 'react';
import PlusIcon from './images/plus.svg';
import { Box_t, Layout_t, Pallet_t, SlipSheet_t, Stack } from 'blackbox';
import { v4 } from 'uuid';

const gItemsPerPage: number = 8;

export function Legend() {
	return (
		<div className="LegendCellContainer">
			<div className="LegendCell">
				<div className="full-size flex-start align-center"></div>
				<div className="LegendItem">
					<span>Name</span>
				</div>
				<div className="full-size flex-center">
					<span>Properties</span>
				</div>
				<div className="full-size flex-start align-center"> </div>
			</div>
		</div>
	);
}

export interface ItemFilterProps {
	pSearchFilter: string;
	pSetSearchFilter(pNewFilter: string): void;
	triggerKeyboard: KeyboardTrigger;
}

export function ItemFilter({
	pSearchFilter,
	pSetSearchFilter,
	triggerKeyboard,
}: ItemFilterProps) {
	const lSearchInputHandlers = getChangeWrapperString(pSetSearchFilter),
		lTriggerKeyboard = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					`Enter search text`,
					pSearchFilter,
					lSearchInputHandlers.Direct
				);
			}
		};
	return (
		<div className="">
			<input
				className="half-padding darker-border rounded background-desert-storm"
				type="text"
				placeholder="Search"
				value={pSearchFilter}
				onChange={lSearchInputHandlers.Event}
				onClick={lTriggerKeyboard}
			/>
		</div>
	);
}

export interface ItemIterator {
	pGet(
		pStartIndex: number,
		pEndOffset: number,
		pSearchFilter: string | undefined
	): [ReactElement[], number] /* Should return summary item cells */;
	triggerKeyboard: KeyboardTrigger;
}

// OPTIONAL FOR NOW.... REMOVE AFTER ROUNDS
interface SummaryScrollProps extends ItemIterator {
	instruction: string;
	AddButton: ButtonProps;
	RightFooterButton: ButtonProps;
	LeftFooterButton: ButtonProps;
	ConfirmFooter?: ConfirmAction;
}

export function SummaryScroll({
	pGet,
	triggerKeyboard,
	instruction,
	AddButton,
	RightFooterButton,
	LeftFooterButton,
	ConfirmFooter,
}: SummaryScrollProps) {
	const lRef = useRef<HTMLDivElement | null>(null);
	const [lCurrentOffset, lSetCurrentOffset] = useState<number>(0);
	const [lSearchFilter, lSetSearchFilter] = useState<string>('');
	const lUpdateSearchFilter = function (pNewFilter: string) {
		if (pNewFilter !== lSearchFilter)
			lSetCurrentOffset(0); /* on search term update reset page */
		lSetSearchFilter(pNewFilter);
	};
	const [lElements, lTotalItems] = useMemo(
		function () {
			return pGet(
				lCurrentOffset * gItemsPerPage,
				gItemsPerPage,
				lSearchFilter.length > 0 ? lSearchFilter : void 0
			);
		},
		[lCurrentOffset, lSearchFilter, pGet]
	);
	const lTotalPages = Math.ceil(lTotalItems / gItemsPerPage);
	const lBackAction = function () {
		if (lCurrentOffset > 0) lSetCurrentOffset(lCurrentOffset - 1);
	};
	const lNextAction = function () {
		if (lCurrentOffset < lTotalPages - 1) lSetCurrentOffset(lCurrentOffset + 1);
	};

	return (
		<div className="full-height-minus-pad full-width-minus-pad grid-row-10">
			<div className="full-size flex-row flex-between align-center">
				<span className="font-5 bold-font color-blue">{instruction}</span>

				<ItemFilter
					pSearchFilter={lSearchFilter}
					pSetSearchFilter={lUpdateSearchFilter}
					triggerKeyboard={triggerKeyboard}
				/>

				{!!AddButton ? (
					<div
						className="flex-row align-center cursor-pointer double-margin-right"
						onClick={AddButton.action}
					>
						<PlusIcon />
						<span className="padding-left large-font hover-underline">
							{AddButton.name}
						</span>
					</div>
				) : (
					<div className="padding"></div>
				)}
			</div>

			<section className="ItemScroll" ref={lRef}>
				{lElements}
			</section>
			{!!RightFooterButton && !!LeftFooterButton && (
				<ContentFooter
					RightButton={RightFooterButton}
					LeftButton={LeftFooterButton}
					confirmAction={ConfirmFooter}
					pCurrentItems={lCurrentOffset}
					pTotalItems={lTotalPages}
					pBackAction={lBackAction}
					pNextAction={lNextAction}
					isSummary={true}
				/>
			)}
		</div>
	);
}

export interface SummaryItemCellProps {
	name: string;
	properties: string;
	trashAction: () => void;
	editAction: () => void;
	disableDelete?: boolean;
  duplicateAction?: () => void;
	children: ReactElement; // the display element.
}

export function SummaryItemCell({
	name,
	properties,
	children,
	trashAction,
	editAction,
	disableDelete,
  duplicateAction
}: SummaryItemCellProps) {
	const lRef = useRef<HTMLDivElement | null>(null);
	return (
		<section className="ItemCellContainer">
			<div className="ItemCell" ref={lRef}>
				<figure className="flex-center full-size">{children}</figure>
				<div className="full-size flex-start align-center padding-left">
					<span className="font-5 color-blue">{name}</span>
				</div>
				<div className="full-size flex-center padding-left">
					<span className="span-display">{properties}</span>
				</div>
				<div className="full-size flex-end align-center">
					<Button
						pClass="double-margin-right width-50 height-56"
						pAction={editAction}
						pClickRed={false}
						pDisabled={false}
					>
						<span className="">Edit</span>
					</Button>
          {
            duplicateAction &&
            <Button
              pClass="font-large flex-center color-blue hover-bold double-margin-right"
              pNormalColorOverride={COLOR.BORDER_GREY}
              pAction={duplicateAction}
            >
              <span className="large-font bold-font icon-copy"></span>
            </Button>
          }
					<Button
						pClass={`font-large flex-center color-red hover-bold double-margin-right ${disableDelete ? 'disabled' : ''}`}
            pNormalColorOverride={COLOR.BORDER_GREY}
						pAction={trashAction}
						pDisabled={disableDelete}
					>
						<span className="large-font bold-font icon-delete"></span>
					</Button>
				</div>
			</div>
		</section>
	);
}

/**
 * Duplicate any Box, Pallet, Layout or Stack configuration
 * and update list of configurations with the duplicate.
 * @param index the configuration index in the list of configurations
 * @param configurations the existing list of configurations
 */
export const duplicateConfiguration = 
  <T extends Box_t | Pallet_t | Layout_t | SlipSheet_t | Stack>
  (
    index: number,
    configurations: T[],
    setConfigurations: (configs: T[]) => void
  ) => 
    () => {
      const curr = configurations[index];
      const clone = JSON.parse(JSON.stringify(curr)) as T;
      clone.id = v4();
      clone.name = duplicateName(
        clone.name,
        new Set(configurations.map(c => c.name))
      );

      const next = [...configurations];
      next.splice(index + 1, 0, clone);

      setConfigurations(next);
    };
  
/**
 * Generate a new unique name if there are duplicates
 * @param name The name being copied 
 * @returns The new, unique name
 */
const duplicateName = (
  name: string,
  existingNames: Set<string>
): string => {
  const baseName = name.replace(/ \(\d+\)$/, '');
  let uniqueName = name;
  let suffix = 1;
  while (existingNames.has(uniqueName)) {
    uniqueName = `${baseName} (${suffix++})`;
  }
  return uniqueName;
};